// Header for the full-screen builders (KAN-763). Phone: Log Job's compact
// header row + the wizard-canon step progress and a "Step n of N · label" caption under it. Tablet/desktop: Log
// Job's crumb bar with the inline numbered stepper in the middle. ONE constant
// title per flow (never the customer's name), and the lead slot is the SAME
// 44x44 box whether it shows the icon tile or the back chevron, so the title
// never shifts when the step changes.
//
// Opt-in EDIT MODE (KAN-778, document previews): with `editMode` the row's
// contents sit in ONE grid cell as two stacked layers - the read row (tile,
// title, Editar text action, ⋯, ✕) and the edit row (Cancelar · title · Listo)
// - and `editMode.active` crossfades them in place (~200ms opacity, ease-out).
// The bar's height, padding and position never change; reduced motion swaps
// instantly. Without `editMode` the header renders exactly as before.
import { ChevronLeft } from 'lucide-react';
import { useIntl } from 'react-intl';
import { foundation } from '../../../ui';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { layoutStyles as L } from '../../../pages/logJobLayoutStyles';
import WizardStepProgress from '../routeOverride/WizardStepProgress';
import { wizardIconTileStyle } from '../wizard/wizardChromeStyles';
import FullScreenInlineStepper from './FullScreenInlineStepper';
import FullScreenHeaderMoreMenu from './FullScreenHeaderMoreMenu';
import OverlayCloseButton from '../overlay/OverlayCloseButton';

const fc = foundation.color;
const fs = foundation.fontSize;
const fw = foundation.fontWeight;

const HIT = 44;
/** View <-> edit crossfade (ms) shared by the header, the number row and the panes. */
export const FULLSCREEN_MODE_FADE_MS = 200;

/**
 * One layer of an in-place crossfade: every layer sits in the same grid cell
 * (gridArea 1/1), so the cell keeps the size of the tallest/widest and nothing
 * re-lays out. The hidden layer goes `visibility: hidden` only after its fade.
 */
export function modeFadeLayer(active, reduced, ms = FULLSCREEN_MODE_FADE_MS) {
  return {
    gridArea: '1 / 1',
    minWidth: 0,
    opacity: active ? 1 : 0,
    visibility: active ? 'visible' : 'hidden',
    pointerEvents: active ? 'auto' : 'none',
    transition: reduced ? 'none' : `opacity ${ms}ms ease-out, visibility 0s linear ${active ? '0ms' : `${ms}ms`}`,
  };
}
const slot = {
  width: HIT,
  height: HIT,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const iconButton = {
  ...slot,
  padding: 0,
  border: 'none',
  background: 'transparent',
  borderRadius: foundation.radii.md,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
};
const S = {
  back: { ...iconButton, color: fc.brand.text },
  title: { margin: 0, fontSize: fs.base, fontWeight: fw.bold, color: fc.neutral.text, lineHeight: 1.2 },
  phoneTitle: { minWidth: 0, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  // Opt-in `eyebrow` (phone): a small muted area label stacked above the title.
  eyebrowStack: { minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 },
  eyebrow: {
    fontSize: fs.xs, fontWeight: fw.medium, color: fc.neutral.textMuted, lineHeight: 1.2,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  // Opt-in `subtitle`: a quiet line with the title — under it on phone/tablet, inline after it on desktop.
  subtitle: {
    fontSize: fs.xs, fontWeight: fw.medium, color: fc.neutral.textSecondary, lineHeight: 1.2,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
  },
  subtitleInline: { fontSize: fs.sm, fontWeight: fw.medium, color: fc.neutral.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 },
  subtitleStack: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 },
  spacer: { flex: 1, minWidth: 0 },
  wrapper: { flexShrink: 0, display: 'flex', flexDirection: 'column' },
  // The step progress carries its own bottom border; the header row drops its.
  phoneRow: { borderBottom: 'none' },
  // One caption line under the phone step dots ("Step 2 of 3 · Details & items");
  // it owns the header's bottom hairline (the dots drop theirs).
  caption: {
    margin: 0,
    padding: `0 ${foundation.spacePx[5]} ${foundation.spacePx[2]}`,
    background: fc.neutral.surface,
    borderBottom: `1px solid ${fc.neutral.border}`,
    color: fc.neutral.textMuted,
    fontSize: fs.xs,
    fontFamily: foundation.fontFamily.sans,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  captionLabel: { color: fc.neutral.text, fontWeight: fw.bold },
  // Edit mode: one grid cell holding the read row and the edit row.
  modeGrid: { display: 'grid', flex: 1, minWidth: 0, alignItems: 'center' },
  modeRow: (gap) => ({ display: 'flex', alignItems: 'center', gap, minWidth: 0 }),
  // The edit row is a 3-column grid with EQUAL side columns, so the title sits
  // on the row's true center whatever the widths of Cancelar and Listo (KAN-782).
  editRow: (gap) => ({ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', columnGap: gap, minWidth: 0 }),
  editStart: { justifySelf: 'start' },
  editEnd: { justifySelf: 'end' },
  // Plain text actions (Editar / Listo blue, Cancelar muted): no box, 44px tall.
  textButton: {
    flexShrink: 0,
    minHeight: HIT,
    padding: `0 ${foundation.spacePx[2]}`,
    border: 'none',
    background: 'transparent',
    borderRadius: foundation.radii.md,
    color: fc.brand.text,
    fontSize: fs.base,
    fontWeight: fw.semibold,
    fontFamily: foundation.fontFamily.sans,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  textButtonMuted: { color: fc.neutral.textSecondary, fontWeight: fw.medium },
  textButtonDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  editTitle: {
    flex: 1,
    minWidth: 0,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

function TextButton({ label, onClick, muted = false, disabled = false, testId }) {
  return (
    <button
      type="button"
      style={{ ...S.textButton, ...(muted ? S.textButtonMuted : null), ...(disabled ? S.textButtonDisabled : null) }}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

/**
 * @param {object} props
 * @param {'phone'|'tablet'|'desktop'} props.layout
 * @param {import('react').ReactNode} [props.icon] - canon icon element for the tile.
 * @param {import('react').ReactNode} props.title - constant for the whole flow.
 * @param {string} [props.areaLabel] - muted crumb ("Estimates"), DESKTOP only: at
 *   tablet width the inline stepper would truncate crumb + title, so tablet shows
 *   the title alone (never truncated at >= 768px).
 * @param {string} [props.eyebrow] - opt-in, PHONE only: a small muted label above the title ("Customers"). Default: none.
 * @param {string} [props.subtitle] - opt-in quiet line with the title: under it on phone/tablet, inline ("Title · sub") on desktop.
 * @param {() => void} [props.onBack] - back chevron in the SAME slot as the tile.
 * @param {() => void} props.onClose
 * @param {boolean} [props.closeDisabled]
 * @param {boolean} [props.hideClose] - omit the ✕ (a page whose only exit is the back chevron, e.g. the phone credit page).
 * @param {string} props.closeAriaLabel
 * @param {string} [props.backAriaLabel]
 * @param {{key: string, label: string}[]} [props.steps]
 * @param {number} [props.currentStep] - 1-based.
 * @param {string} [props.stepperAriaLabel] - optional a11y label for the inline stepper.
 * @param {{items: Array, isMobile?: boolean, ariaLabel?: string, testId?: string, testIdPrefix?: string, onOpenChange?: (open: boolean) => void}} [props.moreMenu]
 *   - opt-in "more actions" ⋯ (44px plain icon button) right before the ✕, opening the app's
 *   ActionMenu. Absent or empty items = the header is unchanged.
 * @param {{label: string, onClick: () => void, disabled?: boolean, testId?: string}} [props.textAction]
 *   - opt-in plain blue text action right before the ⋯ / ✕ (the web "Editar"). Fades with the read row.
 * @param {{active: boolean, title: string, cancelLabel: string, doneLabel: string, onCancel: () => void,
 *   onDone: () => void, disabled?: boolean}} [props.editMode] - opt-in edit mode (see the file header).
 * @param {string} [props.testId]
 */
export default function FullScreenFlowHeader({
  layout,
  icon,
  title,
  areaLabel,
  eyebrow,
  subtitle,
  onBack,
  onClose,
  closeDisabled = false,
  hideClose = false,
  closeAriaLabel,
  backAriaLabel,
  steps,
  currentStep = 1,
  stepperAriaLabel,
  moreMenu,
  textAction,
  editMode,
  testId = 'fullscreen-flow-header',
}) {
  const intl = useIntl();
  const reduced = useReducedMotion();
  const editing = Boolean(editMode?.active);
  const action = textAction ? (
    <TextButton
      label={textAction.label}
      onClick={textAction.onClick}
      disabled={textAction.disabled}
      testId={textAction.testId || `${testId}-text-action`}
    />
  ) : null;
  // Read row + edit row stacked in one cell (only when `editMode` is given).
  // `editInset` evens out a header whose left/right padding differ, so the edit
  // row (and its centered title) is symmetric on the SCREEN, not just in its box.
  const withModes = (readChildren, gap, editInset = null) => {
    if (!editMode) return readChildren;
    return (
      <div style={S.modeGrid} data-testid={`${testId}-modes`} data-mode={editing ? 'edit' : 'read'}>
        <div
          style={{ ...S.modeRow(gap), ...modeFadeLayer(!editing, reduced) }}
          inert={editing}
          aria-hidden={editing || undefined}
          data-testid={`${testId}-read-row`}
        >
          {readChildren}
        </div>
        <div
          style={{ ...S.editRow(gap), ...(editInset ? { paddingLeft: editInset } : null), ...modeFadeLayer(editing, reduced) }}
          inert={!editing}
          aria-hidden={!editing || undefined}
          data-testid={`${testId}-edit-row`}
        >
          <span style={S.editStart}>
            <TextButton label={editMode.cancelLabel} onClick={editMode.onCancel} muted disabled={editMode.disabled} testId={`${testId}-edit-cancel`} />
          </span>
          <h2 style={{ ...S.title, ...S.editTitle }} data-testid={`${testId}-edit-title`}>{editMode.title}</h2>
          <span style={S.editEnd}>
            <TextButton label={editMode.doneLabel} onClick={editMode.onDone} disabled={editMode.disabled} testId={`${testId}-edit-done`} />
          </span>
        </div>
      </div>
    );
  };
  const lead = onBack ? (
    <button type="button" style={S.back} onClick={onBack} aria-label={backAriaLabel} data-testid={`${testId}-back`}>
      <ChevronLeft size={24} strokeWidth={2.25} aria-hidden />
    </button>
  ) : (
    <span style={slot} data-testid={`${testId}-icon`}>
      {icon ? <span style={wizardIconTileStyle} aria-hidden>{icon}</span> : null}
    </span>
  );
  const more = moreMenu ? <FullScreenHeaderMoreMenu {...moreMenu} /> : null;
  // The ONE round close (KAN-1090) - same 44px slot as before, so the row never shifts.
  const close = hideClose ? null : (
    <OverlayCloseButton
      placement="inline"
      onClick={onClose}
      disabled={closeDisabled}
      label={closeAriaLabel}
      testId={`${testId}-close`}
    />
  );

  if (layout === 'phone') {
    const showSteps = steps && steps.length > 1;
    const stepLabel = showSteps ? steps[currentStep - 1]?.label : null;
    const caption = stepLabel
      ? intl.formatMessage(
        { id: 'fullscreenFlow.stepCaption', defaultMessage: 'Step {n} of {total} · {stepLabel}' },
        { n: currentStep, total: steps.length, stepLabel },
      )
      : null;
    // The step label is the caption's tail in every language; bold it when it is.
    const captionHead = caption && caption.endsWith(stepLabel) ? caption.slice(0, caption.length - stepLabel.length) : null;
    return (
      <div style={S.wrapper} data-testid={testId} data-layout="phone">
        <header style={{ ...L.compactHeader, ...S.phoneRow }} data-cvx-overlay-header="">
          {withModes(
            <>
              {lead}
              {eyebrow || subtitle ? (
                <div style={S.eyebrowStack}>
                  {eyebrow ? <span style={S.eyebrow} data-testid={`${testId}-eyebrow`}>{eyebrow}</span> : null}
                  <h2 style={{ ...S.title, ...S.phoneTitle, flex: 'none' }} data-testid={`${testId}-title`}>{title}</h2>
                  {subtitle ? <span style={S.subtitle} data-testid={`${testId}-subtitle`}>{subtitle}</span> : null}
                </div>
              ) : (
                <h2 style={{ ...S.title, ...S.phoneTitle }} data-testid={`${testId}-title`}>{title}</h2>
              )}
              {action}
              {more}
              {close}
            </>,
            L.compactHeader.gap,
            // compactHeader pads 8px left / 12px right: inset the edit row 4px left.
            `calc(${L.compactHeader.paddingRight} - ${L.compactHeader.paddingLeft})`,
          )}
        </header>
        {showSteps ? <WizardStepProgress compact animated borderless={Boolean(caption)} steps={steps} currentStep={currentStep} /> : null}
        {caption ? (
          <p style={S.caption} data-testid={`${testId}-step-caption`}>
            {captionHead != null ? <>{captionHead}<span style={S.captionLabel}>{stepLabel}</span></> : caption}
          </p>
        ) : null}
      </div>
    );
  }

  const crumbRow = (
    <>
      {lead}
      <div style={layout === 'tablet' ? { ...L.crumbText, flexShrink: 0 } : L.crumbText}>
        {areaLabel && layout === 'desktop' ? (
          <>
            <span style={{ ...L.crumbName }} data-testid={`${testId}-area`}>{areaLabel}</span>
            <span style={L.crumbSep} aria-hidden>/</span>
          </>
        ) : null}
        {subtitle && layout === 'tablet' ? (
          <div style={S.subtitleStack}>
            <h2 style={{ ...S.title, ...L.crumbName }} data-testid={`${testId}-title`}>{title}</h2>
            <span style={S.subtitle} data-testid={`${testId}-subtitle`}>{subtitle}</span>
          </div>
        ) : (
          <h2 style={{ ...S.title, ...L.crumbName }} data-testid={`${testId}-title`}>{title}</h2>
        )}
        {subtitle && layout !== 'tablet' ? (
          <>
            <span style={L.crumbSep} aria-hidden>·</span>
            <span style={S.subtitleInline} data-testid={`${testId}-subtitle`}>{subtitle}</span>
          </>
        ) : null}
      </div>
      <span style={S.spacer} />
      {steps && steps.length > 1 ? (
        <FullScreenInlineStepper steps={steps} currentStep={currentStep} ariaLabel={stepperAriaLabel} />
      ) : null}
      <span style={S.spacer} />
      {action}
      {more}
      {close}
    </>
  );
  return (
    <header style={{ ...L.crumbBar, justifyContent: 'flex-start' }} data-cvx-overlay-header="" data-testid={testId} data-layout={layout}>
      {withModes(crumbRow, L.crumbBar.gap)}
    </header>
  );
}
